<script lang="ts" setup name="OrganisationPortCallsDetailsCargo">
import { computed, onMounted, reactive, ref } from 'vue';

import {
  PORT_CALLS_CARGO_ACTIVITY_WIDGET_TYPES_TO_TITLE,
  PORT_CALLS_CARGO_TERMS_TYPES,
  PORT_CALLS_CARGO_TYPE_TO_TEXT,
  PORT_CALLS_CARGO_WIDGET_TYPES,
} from '~/constants/organisationPortCalls';

import { formatNumber } from '~/features/useNumbers';
import { useService } from '~/features/useService';
import { getIdsFromArray } from '~/features/useUrlSearch';
import { isEmpty } from '~/features/useUtilities';

import Service from '~/services/Service';

import BaseDisplayMetric from '~/components/BaseDisplayMetric.vue';
import DataTable from '~/components/DataTable.vue';
import DataTableLoading from '~/components/DataTableLoading.vue';
import OrganisationPortCallsDetailsCargoFilters from '~/components/OrganisationPortCallsDetailsCargoFilters.vue';
import PortCallsDetailsCargoTypeTag from '~/components/PortCallsDetailsCargoTypeTag.vue';
import ServiceCard from '~/components/ServiceCard.vue';

import type {
  IDataTableSettings,
  IPortCallsCargoActivityWidgets,
  IPortCallsCargoActivityWidgetTypes,
  IPortCallsCargoQuery,
  IPortCallsCargoRow,
  PortCallsCargoTermsType,
} from '~/types';

const { portId, organisationId } = defineProps<{
  portId: string;
  organisationId: OrganisationId;
}>();

const isWidgetsLoading = ref(false);

const filtersQuery = reactive<IPortCallsCargoQuery>({
  ports: [],
  types: [],
});

const filterOptions = reactive<IPortCallsCargoQuery>({
  ports: [],
  types: [],
});

const widgetsData = ref<IPortCallsCargoActivityWidgets>({
  transit: 0,
  discharge: 0,
  load: 0,
});

const onSearchClear = () => {
  filtersQuery.ports = [];
  filtersQuery.types = [];
};

const toWidget = (key: IPortCallsCargoActivityWidgetTypes) => {
  return {
    id: key as string,
    title: PORT_CALLS_CARGO_ACTIVITY_WIDGET_TYPES_TO_TITLE[key] as string,
    value: formatNumber(widgetsData.value?.[key] ?? 0, {
      maximumFractionDigits: 0,
    }),
    description: 'MT',
  };
};

const items = computed(() => PORT_CALLS_CARGO_WIDGET_TYPES.map(toWidget));

const tableRows = ref<IPortCallsCargoRow[]>([]);

const fetchFilters = () => {
  Service.organisation(organisationId)
    .portCallsCalls()
    .cargoLineUp(portId)
    .filters()
    .onSuccess(({ data }: { data: IPortCallsCargoQuery }) => {
      filterOptions.ports = data.ports;
      filterOptions.types = data.types.map((type) => ({
        ...type,
        name: PORT_CALLS_CARGO_TYPE_TO_TEXT[type.id],
      }));
    })
    .execute();
};

const fetchWidgets = () => {
  Service.organisation(organisationId)
    .portCallsCalls()
    .cargoLineUp(portId)
    .widgets()
    .onStart(() => {
      isWidgetsLoading.value = true;
    })
    .onSuccess(({ data }: { data: IPortCallsCargoActivityWidgets }) => {
      widgetsData.value = data;
      isWidgetsLoading.value = false;
    })
    .onFinish(() => {
      isWidgetsLoading.value = false;
    })
    .execute();
};

const serviceState = useService(
  Service.organisation(organisationId)
    .portCallsCalls()
    .cargoLineUp(portId)
    .index(),
  ({ data }: { data: IPortCallsCargoRow[] }) => {
    if (isEmpty(data)) return false;
    tableRows.value = data;
    return true;
  },
  {
    loading: 'skeleton',
    cancelOnNewRequest: true,
    paginated: true,
    query: filtersQuery,
    getQuery: () => ({
      ports: getIdsFromArray(filtersQuery.ports),
      types: getIdsFromArray(filtersQuery.types),
    }),
  },
);

const tableSettings: IDataTableSettings<IPortCallsCargoRow> = {
  editable: false,
  pagination: true,
  columns: [
    { header: 'Commodity', key: 'commodity' },
    {
      header: 'Shipper',
      key: 'shipper',
    },
    {
      header: 'Type',
      key: 'type',
      render: () => PortCallsDetailsCargoTypeTag,
    },
    {
      header: 'Terms',
      key: 'terms',
      fallback: 'NA',
      valueFormatter: (value: PortCallsCargoTermsType) => {
        const TYPE_TO_TEXT = {
          [PORT_CALLS_CARGO_TERMS_TYPES.LINEAR]: 'Linear',
          [PORT_CALLS_CARGO_TERMS_TYPES.FREE_IN_OUT]: 'Free In/Out',
        };

        return TYPE_TO_TEXT[value];
      },
    },
    {
      header: 'Load Port',
      key: 'loadPort',
    },
    {
      header: 'Destination',
      key: 'dischargePort',
    },
    {
      header: 'Cargo Qty',
      key: 'cargoQty',
      valueFormatter: (value: number) => `${formatNumber(value)} MT`,
    },
  ],
};

onMounted(() => {
  fetchFilters();
  fetchWidgets();
});
</script>

<template>
  <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
    <BaseDisplayMetric
      v-for="item in items"
      :key="item.id"
      v-bind="item"
      :loading="isWidgetsLoading"
    />
  </div>

  <div
    class="mt-2 divide-y divide-charcoal-2 overflow-hidden rounded-md bg-white shadow"
  >
    <div class="p-6">
      <h3 class="py-2 text-subtitle-1">Cargo Line-Up</h3>
    </div>

    <div
      v-if="!serviceState.stateHasErrors"
      class="divide-y divide-charcoal-2 overflow-hidden bg-white shadow"
    >
      <OrganisationPortCallsDetailsCargoFilters
        v-model="filtersQuery"
        v-bind="filterOptions"
      />
    </div>

    <ServiceCard
      noResultsMessage="Please amend search criteria to see available data."
      noDataTitle="This Is Where You’ll See Your Cargo Line-Up"
      noDataMessage="The cargo line-up will appear here once it's ready."
      v-bind="serviceState"
      styled
      @clear="onSearchClear"
    >
      <div
        class="divide-y divide-charcoal-2 overflow-hidden rounded-md bg-white shadow"
      >
        <DataTableLoading v-if="serviceState.stateIsLoading" />

        <DataTable v-else :data="tableRows" :settings="tableSettings" />
      </div>
    </ServiceCard>
  </div>
</template>
